/**
 * Master Muscles — compatibility UI
 * ---------------------------------------------------------------------------
 * Runs after mm-compat.js. Turns the capability facts that layer detected into
 * something the customer can act on, instead of a button that silently fails:
 *
 *   1. Cookies blocked      -> banner explaining the cart cannot be saved
 *   2. In-app WebView       -> dismissible "open in your browser" prompt
 *   3. Cart request failure -> a visible toast rather than a dead button
 *   4. Cart mutation bus    -> badge counts stay correct no matter which of the
 *                              theme's add-to-cart paths performed the change
 *
 * Strings come from window.MM_COMPAT_I18N (set by snippets/mm-compat-notice).
 */
(function (window, document) {
  'use strict';

  if (!window.MMCompat) return;
  if (window.__mmCompatUi) return;
  window.__mmCompatUi = true;

  var C = window.MMCompat;
  var env = C.env;
  var T = window.MM_COMPAT_I18N || {};

  function t(key, fallback) {
    return T[key] || fallback;
  }

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function mountTop(node) {
    var host = document.getElementById('mm-compat-notices');
    if (host) {
      host.appendChild(node);
      return;
    }
    var body = document.body;
    if (body) body.insertBefore(node, body.firstChild);
  }

  /* =========================================================================
   * 1. Cookies blocked — the cart physically cannot persist
   * =======================================================================*/

  function cookieBanner() {
    if (env.cookies) return;

    var wrap = el('div', 'mm-compat-banner mm-compat-banner--warning');
    wrap.setAttribute('role', 'status');
    wrap.appendChild(
      el(
        'span',
        'mm-compat-banner__text',
        t(
          'cookies_blocked',
          'Your browser is blocking cookies, so items cannot stay in the cart. Please enable cookies or open the store in Safari or Chrome.'
        )
      )
    );
    mountTop(wrap);
  }

  /* =========================================================================
   * 2. In-app WebView — offer the real browser
   * -------------------------------------------------------------------------
   * Instagram, Facebook, TikTok and Snapchat render the store inside a WebView
   * that restricts storage and cannot present Apple Pay or Google Pay. Rather
   * than force a redirect we make the escape hatch obvious and dismissible.
   * =======================================================================*/

  var DISMISS_KEY = 'mm_inapp_prompt_dismissed';

  function openExternally() {
    var url = window.location.href;
    if (env.android) {
      // Android intent:// hands the URL to the user's default browser.
      var noScheme = url.replace(/^https?:\/\//, '');
      window.location.href =
        'intent://' + noScheme + '#Intent;scheme=https;action=android.intent.action.VIEW;end';
      return;
    }
    // iOS WebViews cannot be escaped programmatically; copying the link is the
    // only reliable path, so tell the customer what happened.
    C.copyText(url)
      .then(function () {
        toast(t('link_copied', 'Link copied — paste it into Safari to continue.'), false);
      })
      .catch(function () {
        toast(t('open_manually', 'Open this page in Safari using the browser menu.'), false);
      });
  }

  function inAppPrompt() {
    if (!env.isInApp) return;
    if (C.storage.get(DISMISS_KEY) === '1') return;

    var wrap = el('div', 'mm-compat-banner mm-compat-banner--info');
    wrap.setAttribute('role', 'status');

    var text = el(
      'span',
      'mm-compat-banner__text',
      t('inapp_hint', 'For a faster checkout with Apple Pay and Benefit, open the store in your browser.')
    );

    var openBtn = el('button', 'mm-compat-banner__btn', t('inapp_open', 'Open in browser'));
    openBtn.type = 'button';
    openBtn.addEventListener('click', openExternally);

    var closeBtn = el('button', 'mm-compat-banner__close', '&times;');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', t('dismiss', 'Dismiss'));
    closeBtn.addEventListener('click', function () {
      C.storage.set(DISMISS_KEY, '1');
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    });

    wrap.appendChild(text);
    wrap.appendChild(openBtn);
    wrap.appendChild(closeBtn);
    mountTop(wrap);
  }

  /* =========================================================================
   * 3. Visible failures
   * =======================================================================*/

  var toastTimer = null;

  function toast(message, isError) {
    // Deliberately not delegating to MMCart.showToast: it takes a string type
    // rather than a boolean and no-ops when the cart sheet is not rendered,
    // which would swallow the message on pages without the sheet.
    if (window.MMShop && typeof window.MMShop.showToast === 'function') {
      window.MMShop.showToast(message, !!isError);
      return;
    }
    var node = document.getElementById('mmCompatToast');
    if (!node) {
      node = el('div', 'mm-compat-toast');
      node.id = 'mmCompatToast';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      document.body.appendChild(node);
    }
    node.textContent = message;
    node.className = 'mm-compat-toast' + (isError ? ' mm-compat-toast--error' : '');
    node.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      node.classList.remove('is-visible');
    }, 4000);
  }

  document.addEventListener('mm:error', function (e) {
    var detail = e && e.detail;
    if (!detail) return;
    var msg = String(detail.message || '');

    if (/cart-not-persisted/i.test(msg)) {
      toast(
        t(
          'cart_not_saved',
          'The cart could not be saved. Your browser is blocking cookies — open the store in Safari or Chrome.'
        ),
        true
      );
      return;
    }
    if (/Cart request timed out/i.test(msg)) {
      toast(t('cart_timeout', 'The connection is slow. Please try again.'), true);
      return;
    }
    if (/Cart API/i.test(msg)) {
      toast(t('cart_error', 'Could not update the cart. Please try again.'), true);
    }
  });

  /* =========================================================================
   * 4. Badge sync from the single cart-mutation bus
   * -------------------------------------------------------------------------
   * The theme has several independent add-to-cart implementations. Whichever
   * one ran, the bus fires, so the badge is refreshed from the real cart.
   * =======================================================================*/

  var BADGE_SEL = '.cart-count,[data-cart-count],.mm-cart-count,.mm-cart-badge,.mm-bottom-nav-badge';
  var badgeTimer = null;

  /**
   * Defer to the cart sheet's own updater when it is present, so the two never
   * disagree about hidden-at-zero badges; only act as a fallback otherwise.
   */
  function paintBadges(count) {
    var n = parseInt(count, 10) || 0;

    if (window.MMCart && typeof window.MMCart.updateBadges === 'function') {
      window.MMCart.updateBadges(n, false);
      return;
    }

    var nodes = document.querySelectorAll(BADGE_SEL);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      node.textContent = String(n);
      if (node.classList.contains('mm-cart-badge') || node.classList.contains('mm-bottom-nav-badge')) {
        node.style.display = n > 0 ? 'flex' : 'none';
      }
    }
  }

  function syncBadges() {
    clearTimeout(badgeTimer);
    badgeTimer = setTimeout(function () {
      C.getCart()
        .then(function (cart) {
          if (cart && typeof cart.item_count === 'number') paintBadges(cart.item_count);
        })
        .catch(function () {});
    }, 250);
  }

  C.onCartMutation(syncBadges);
  document.addEventListener('mm:cart-changed', function (e) {
    var cart = e && e.detail && e.detail.cart;
    if (cart && typeof cart.item_count === 'number') paintBadges(cart.item_count);
  });

  /* =========================================================================
   * Boot
   * =======================================================================*/

  function init() {
    try {
      cookieBanner();
      inAppPrompt();
    } catch (e) {
      C.report(e, 'mm-compat-ui.init');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.MMCompatUI = { toast: toast, openExternally: openExternally };
})(window, document);
